// Browser-side error reporter, shared by components/ErrorLogger (uncaught
// errors) and the app/error.tsx + app/global-error.tsx boundaries (failed
// renders). Import-free of anything server-side so it is safe in any client
// component.
//
// Deliberately silent: every call is best-effort, and a failure to report is
// swallowed — a visitor must never see a second error caused by logging the
// first one.

export type ReportKind = "client" | "render" | "resource" | "promise";

/** Same random per-tab id the analytics tracker uses, when one exists. */
function sessionId(): string | null {
  try {
    return sessionStorage.getItem("ts_sid");
  } catch {
    return null;
  }
}

function device(): "desktop" | "mobile" | "tablet" {
  const w = window.innerWidth;
  return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

// The same fault can fire on every render or every mouse move; only report a
// given message once per page load.
const seen = new Set<string>();

export function reportError(input: {
  kind: ReportKind;
  message: string;
  source?: string | null;
  stack?: string | null;
  path?: string;
}): void {
  try {
    if (typeof window === "undefined") return;
    const path = input.path ?? window.location.pathname;
    const key = `${input.kind}|${input.message}|${input.source ?? ""}|${path}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (seen.size > 50) return; // runaway page — stop reporting

    const body = JSON.stringify({
      kind: input.kind,
      message: String(input.message).slice(0, 500),
      source: input.source ? String(input.source).slice(0, 400) : null,
      stack: input.stack ? String(input.stack).slice(0, 4000) : null,
      path,
      device: device(),
      sid: sessionId(),
    });
    if (!navigator.sendBeacon?.("/api/log-error", new Blob([body], { type: "application/json" }))) {
      fetch("/api/log-error", {
        method: "POST",
        body,
        keepalive: true,
        headers: { "Content-Type": "application/json" },
      }).catch(() => {});
    }
  } catch {
    /* reporting must never throw */
  }
}
